import { DateTime } from 'luxon'
import { makeAutoObservable } from 'mobx'
import GasConsumption from 'src/models/GasConsumption'
import apiClient from 'src/utils/apiClient'
import {
  DailyParams as _DailyParams,
  HourlyParams as _HourlyParams,
  MonthlyParams as _MonthlyParams,
  dailyDateFormat,
  hourlyDateFormat,
  monthlyDateFormat,
} from 'src/utils/apiClient/gasConsumptions'

type DailyParams = Omit<_DailyParams, 'fromDate' | 'toDate'> & {
  fromDate: DateTime
  toDate: DateTime
}
type HourlyParams = Omit<_HourlyParams, 'fromDate' | 'toDate'> & {
  fromDate: DateTime
  toDate: DateTime
}
type MonthlyParams = Omit<_MonthlyParams, 'fromDate' | 'toDate'> & {
  fromDate: DateTime
  toDate: DateTime
}
type CacheKeyParams = { fromDate: DateTime; toDate: DateTime }

const CACHE_SIZE = 10

export default class GasConsumptionStore {
  private caches = {
    daily: new Map<string, GasConsumption>(),
    hourly: new Map<string, GasConsumption>(),
    monthly: new Map<string, GasConsumption>(),
  }

  // Constructor ------------

  constructor() {
    makeAutoObservable(this)
  }

  // Actions ---------------

  getDailyConsumptions = async (params: DailyParams) => {
    const cacheKey = this.cacheKey(params)
    const cache = this.caches.daily.get(cacheKey)
    if (cache) return cache

    const res = await apiClient.gasConsumptions.getDailyConsumptions({
      ...params,
      fromDate: params.fromDate.toFormat(dailyDateFormat),
      toDate: params.toDate.toFormat(dailyDateFormat),
    })

    const data = new GasConsumption(res)
    this.caches.daily.set(cacheKey, data)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.daily.size > CACHE_SIZE) this.caches.daily.delete(this.caches.daily.keys().next().value)

    return data
  }

  getHourlyConsumptions = async (params: HourlyParams) => {
    const cacheKey = this.cacheKey(params)
    const cache = this.caches.hourly.get(cacheKey)
    if (cache) return cache

    const res = await apiClient.gasConsumptions.getHourlyConsumptions({
      ...params,
      fromDate: params.fromDate.toFormat(hourlyDateFormat),
      toDate: params.toDate.toFormat(hourlyDateFormat),
    })

    const data = new GasConsumption(res)
    this.caches.hourly.set(cacheKey, data)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.hourly.size > CACHE_SIZE) this.caches.hourly.delete(this.caches.hourly.keys().next().value)

    return data
  }

  getMonthlyConsumptions = async (params: MonthlyParams) => {
    const cacheKey = this.cacheKey(params)
    const cache = this.caches.monthly.get(cacheKey)
    if (cache) return cache

    const res = await apiClient.gasConsumptions.getMonthlyConsumptions({
      ...params,
      fromDate: params.fromDate.toFormat(monthlyDateFormat),
      toDate: params.toDate.toFormat(monthlyDateFormat),
    })

    const data = new GasConsumption(res)
    this.caches.monthly.set(cacheKey, data)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.monthly.size > CACHE_SIZE) this.caches.monthly.delete(this.caches.monthly.keys().next().value)

    return data
  }

  // Private actions ---------------

  cacheKey = ({ fromDate, toDate }: CacheKeyParams) => {
    const format = 'yyyyMMddHHmmss'
    return `${fromDate.toFormat(format)}-${toDate.toFormat(format)}`
  }
}
